<template>
  <div
    class="min-h-full bg-transparent dark:bg-[#0B1120] pb-28 text-slate-800 dark:text-[#F1F5F9] font-sans antialiased"
  >
    <main
      class="mx-auto max-w-7xl px-3.5 sm:px-6 lg:px-8 py-5 sm:py-8 space-y-6 sm:space-y-8"
    >
      <!-- LOADING SKELETON -->
      <div
        v-if="isLoading"
        class="space-y-6"
      >
        <div
          class="hidden md:block h-64 w-full animate-pulse rounded-3xl bg-slate-200/70"
        ></div>
        <div
          class="h-96 w-full animate-pulse rounded-3xl bg-slate-200/70"
        ></div>
      </div>

      <div
        v-else
        class="space-y-6 sm:space-y-8"
      >
        <!-- =====================================================
             SECTION 1: LIVE EDITORIAL PREVIEWS (DESKTOP & TABLET ONLY)
        ===================================================== -->
        <section class="hidden md:block space-y-4">
          <div class="flex items-center justify-between px-1">
            <div class="flex items-center gap-2.5">
              <span class="relative flex h-2.5 w-2.5">
                <span
                  class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
                ></span>
                <span
                  class="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"
                ></span>
              </span>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5"
              >
                <EyeIcon class="h-4 w-4 text-indigo-600" />
                Live Front-End Simulation
              </h3>
            </div>

            <div class="flex items-center gap-2">
              <span
                class="rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700"
              >
                Realtime Mirror
              </span>
            </div>
          </div>

          <!-- 1. Prospectus Download Band Live Preview -->
          <div
            class="rounded-3xl border border-slate-900 bg-[#11151F] p-6 sm:p-10 text-white shadow-md relative overflow-hidden"
          >
            <div
              class="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10"
            >
              <div class="space-y-2 max-w-xl">
                <div class="inline-flex items-center gap-2">
                  <span class="w-4 h-[1.5px] bg-[#B8863B]"></span>
                  <span
                    class="font-mono text-[10px] font-bold tracking-widest text-[#D8B372] uppercase"
                  >
                    Resource Downloads
                  </span>
                </div>
                <h3
                  class="font-display font-semibold text-xl sm:text-2xl text-white"
                >
                  {{
                    formData.prospectus.title || "Download Prospectus 2026–27"
                  }}
                </h3>
                <p
                  class="font-serif italic text-sm sm:text-base text-slate-300"
                >
                  {{
                    formData.prospectus.description ||
                    "Curriculum, admissions and campus life — all in one place."
                  }}
                </p>
              </div>

              <div class="flex items-center gap-3 shrink-0 flex-wrap">
                <div
                  class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#B8863B] text-[#11151F] text-xs font-bold shadow-sm"
                >
                  <DownloadIcon class="h-4 w-4" />
                  <span>Download Prospectus</span>
                </div>
                <div
                  class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/30 text-white text-xs font-bold hover:bg-white/10 transition"
                >
                  <BookOpenIcon class="h-4 w-4 text-[#B8863B]" />
                  <span>Browse Almanac</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 2. Footer Section Live Simulation -->
          <div
            class="rounded-3xl border border-slate-900 bg-[#11151F] p-6 sm:p-10 text-slate-300 shadow-md relative overflow-hidden space-y-8"
          >
            <div class="flex items-center gap-2 border-b border-white/10 pb-3">
              <span class="w-3 h-0.5 bg-[#B8863B]"></span>
              <h4
                class="font-mono text-[11px] font-bold uppercase tracking-wider text-[#D8B372]"
              >
                Footer Structure Preview
              </h4>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
              <!-- Brand & Note Column -->
              <div class="md:col-span-5 space-y-4">
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-full bg-[#B8863B] text-[#11151F] font-bold flex items-center justify-center text-sm shadow-sm"
                  >
                    SCH
                  </div>
                  <div>
                    <h5 class="font-bold text-white text-base">
                      Institution Name
                    </h5>
                    <p class="text-[11px] font-mono text-[#D8B372]">
                      Official Portal
                    </p>
                  </div>
                </div>
                <p class="text-xs text-slate-400 leading-relaxed max-w-sm">
                  {{
                    formData.footer.managementNote ||
                    "Managed under the superintendency of the governing education society, carrying forward the highest standards of holistic education."
                  }}
                </p>
              </div>

              <!-- Useful Links Preview -->
              <div class="md:col-span-3 space-y-3">
                <h6
                  class="font-mono text-[10.5px] font-bold uppercase tracking-widest text-[#D8B372]"
                >
                  Useful Links
                </h6>
                <ul class="space-y-2 text-xs">
                  <li
                    v-for="(link, lIdx) in formData.footer.usefulLinks.slice(
                      0,
                      3,
                    )"
                    :key="lIdx"
                  >
                    <span
                      class="hover:text-[#D8B372] transition cursor-pointer"
                      >{{ link.label || "Link Label" }}</span
                    >
                  </li>
                  <li
                    v-if="formData.footer.usefulLinks.length === 0"
                    class="text-slate-500 italic"
                  >
                    No links configured
                  </li>
                </ul>
              </div>

              <!-- Academics Links Preview -->
              <div class="md:col-span-4 space-y-3">
                <h6
                  class="font-mono text-[10.5px] font-bold uppercase tracking-widest text-[#D8B372]"
                >
                  Academics
                </h6>
                <ul class="space-y-2 text-xs">
                  <li
                    v-for="(link, aIdx) in formData.footer.academicLinks.slice(
                      0,
                      3,
                    )"
                    :key="aIdx"
                  >
                    <span
                      class="hover:text-[#D8B372] transition cursor-pointer"
                      >{{ link.label || "Academic Link" }}</span
                    >
                  </li>
                  <li
                    v-if="formData.footer.academicLinks.length === 0"
                    class="text-slate-500 italic"
                  >
                    No academic links configured
                  </li>
                </ul>
              </div>
            </div>

            <!-- Footer Bottom Bar -->
            <div
              class="pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] font-mono text-slate-500"
            >
              <span>{{
                formData.footer.copyrightText ||
                "© 2026 Institution. All rights reserved."
              }}</span>
              <span class="text-[#D8B372] font-bold">{{
                formData.footer.poweredByText || "Powered by Educanium"
              }}</span>
            </div>
          </div>
        </section>

        <!-- =====================================================
             SECTION 2: EDITORIAL FORMS (RESPONSIVE FOR ALL SCREENS)
        ===================================================== -->
        <div
          class="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-start"
        >
          <!-- LEFT COLUMN: PROSPECTUS CONFIGURATION -->
          <div class="lg:col-span-6 space-y-5 sm:space-y-6">
            <section
              class="rounded-3xl border border-slate-200/90 bg-white p-4 sm:p-7 shadow-xs space-y-4 sm:space-y-5"
            >
              <div
                class="flex items-center space-x-2.5 border-b border-slate-100 pb-3.5 sm:pb-4"
              >
                <div
                  class="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"
                >
                  <FileTextIcon class="h-4 w-4" />
                </div>
                <div>
                  <h2
                    class="text-sm sm:text-base font-bold text-slate-900 leading-tight"
                  >
                    Prospectus & Download Band
                  </h2>
                  <p class="text-[11px] text-slate-500">
                    Manage downloadable documents and promotional banner copy
                  </p>
                </div>
              </div>

              <div class="space-y-3.5 sm:space-y-4">
                <div>
                  <label
                    class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                    >Band Heading Title</label
                  >
                  <input
                    v-model="formData.prospectus.title"
                    type="text"
                    placeholder="Download Prospectus 2026–27"
                    class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs font-bold text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                  />
                </div>

                <div>
                  <label
                    class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                    >Band Description</label
                  >
                  <textarea
                    v-model="formData.prospectus.description"
                    rows="2"
                    placeholder="Curriculum, admissions and campus life — all in one place."
                    class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs text-slate-700 leading-relaxed focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                  ></textarea>
                </div>

                <!-- Prospectus File Upload / URL -->
                <div class="pt-2 border-t border-slate-100 space-y-3">
                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                      >Prospectus Document (PDF / File)</label
                    >
                    <div class="flex items-center gap-2">
                      <input
                        v-model="formData.prospectus.fileUrl"
                        type="text"
                        placeholder="https://... or upload file below"
                        class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                      />
                    </div>
                  </div>

                  <div class="flex items-center gap-3">
                    <label
                      class="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-200 cursor-pointer transition"
                    >
                      <UploadIcon class="h-3.5 w-3.5 text-slate-500" />
                      <span>Choose Prospectus File</span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        class="hidden"
                        @change="(e) => handleFileSelect(e, 'prospectus')"
                      />
                    </label>
                    <span class="text-[11px] text-slate-500 truncate">
                      {{
                        prospectusFile
                          ? prospectusFile.name
                          : formData.prospectus.fileUrl
                          ? "File linked"
                          : "No file chosen"
                      }}
                    </span>
                  </div>
                </div>

                <!-- Almanac File Upload / URL -->
                <div class="pt-2 border-t border-slate-100 space-y-3">
                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                      >Almanac / Handbook Document</label
                    >
                    <input
                      v-model="formData.prospectus.almanacUrl"
                      type="text"
                      placeholder="https://... or upload file below"
                      class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                    />
                  </div>

                  <div class="flex items-center gap-3">
                    <label
                      class="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-200 cursor-pointer transition"
                    >
                      <UploadIcon class="h-3.5 w-3.5 text-slate-500" />
                      <span>Choose Almanac File</span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        class="hidden"
                        @change="(e) => handleFileSelect(e, 'almanac')"
                      />
                    </label>
                    <span class="text-[11px] text-slate-500 truncate">
                      {{
                        almanacFile
                          ? almanacFile.name
                          : formData.prospectus.almanacUrl
                          ? "File linked"
                          : "No file chosen"
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <!-- RIGHT COLUMN: FOOTER CONFIGURATION BUILDER -->
          <div class="lg:col-span-6 space-y-5 sm:space-y-6">
            <section
              class="rounded-3xl border border-slate-200/90 bg-white p-4 sm:p-7 shadow-xs space-y-5"
            >
              <div
                class="flex items-center space-x-2.5 border-b border-slate-100 pb-3.5 sm:pb-4"
              >
                <div
                  class="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"
                >
                  <LayoutTemplateIcon class="h-4 w-4" />
                </div>
                <div>
                  <h2
                    class="text-sm sm:text-base font-bold text-slate-900 leading-tight"
                  >
                    Footer Customization
                  </h2>
                  <p class="text-[11px] text-slate-500">
                    Governance note, copyright, and navigation link columns
                  </p>
                </div>
              </div>

              <!-- General Footer Settings -->
              <div class="space-y-3.5">
                <div>
                  <label
                    class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                    >Superintendency / Management Note</label
                  >
                  <textarea
                    v-model="formData.footer.managementNote"
                    rows="2"
                    placeholder="Managed under the superintendency of the education society..."
                    class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-xs text-slate-700 leading-relaxed focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                  ></textarea>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                      >Copyright Text</label
                    >
                    <input
                      v-model="formData.footer.copyrightText"
                      type="text"
                      placeholder="© 2026 School. All rights reserved."
                      class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                    />
                  </div>

                  <div>
                    <label
                      class="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1"
                      >Powered By Label</label
                    >
                    <input
                      v-model="formData.footer.poweredByText"
                      type="text"
                      placeholder="Powered by Educanium"
                      class="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs text-slate-800 focus:bg-white focus:border-indigo-500 focus:outline-none transition"
                    />
                  </div>
                </div>
              </div>

              <!-- Useful Links Builder -->
              <div class="pt-4 border-t border-slate-100 space-y-3">
                <div class="flex items-center justify-between">
                  <h3
                    class="text-xs font-bold text-slate-800 uppercase tracking-wider"
                  >
                    Useful Links Column
                  </h3>
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-bold hover:bg-indigo-100 transition cursor-pointer"
                    @click="addLink('useful')"
                  >
                    <PlusIcon class="h-3.5 w-3.5" /> Add Link
                  </button>
                </div>

                <div class="space-y-2">
                  <div
                    v-for="(link, uIdx) in formData.footer.usefulLinks"
                    :key="uIdx"
                    class="flex items-center gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200/80"
                  >
                    <input
                      v-model="link.label"
                      type="text"
                      placeholder="Label"
                      class="w-1/3 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-800 focus:outline-none"
                    />
                    <input
                      v-model="link.url"
                      type="text"
                      placeholder="URL (#about)"
                      class="flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-800 focus:outline-none"
                    />
                    <button
                      type="button"
                      class="p-1 text-slate-400 hover:text-rose-600 transition cursor-pointer"
                      @click="removeLink('useful', uIdx)"
                    >
                      <Trash2Icon class="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- Academic Links Builder -->
              <div class="pt-4 border-t border-slate-100 space-y-3">
                <div class="flex items-center justify-between">
                  <h3
                    class="text-xs font-bold text-slate-800 uppercase tracking-wider"
                  >
                    Academics Column Links
                  </h3>
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-bold hover:bg-indigo-100 transition cursor-pointer"
                    @click="addLink('academic')"
                  >
                    <PlusIcon class="h-3.5 w-3.5" /> Add Link
                  </button>
                </div>

                <div class="space-y-2">
                  <div
                    v-for="(link, aIdx) in formData.footer.academicLinks"
                    :key="aIdx"
                    class="flex items-center gap-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200/80"
                  >
                    <input
                      v-model="link.label"
                      type="text"
                      placeholder="Label"
                      class="w-1/3 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-800 focus:outline-none"
                    />
                    <input
                      v-model="link.url"
                      type="text"
                      placeholder="URL (#)"
                      class="flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-800 focus:outline-none"
                    />
                    <button
                      type="button"
                      class="p-1 text-slate-400 hover:text-rose-600 transition cursor-pointer"
                      @click="removeLink('academic', aIdx)"
                    >
                      <Trash2Icon class="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <!-- =====================================================
           COMPACT STICKY BOTTOM ACTION BAR
      ================================================     -->
      <!-- COMPACT STICKY BOTTOM ACTION BAR (STICKY TO BOTTOM RIGHT ALWAYS) -->
      <teleport to="body">
        <div
          class="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 lg:right-8 z-[900] max-w-[calc(100vw-2rem)] sm:max-w-md pointer-events-auto"
        >
          <div
            class="rounded-2xl bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl border border-slate-200/90 dark:border-[#263449] p-2.5 sm:p-3 shadow-2xl shadow-slate-900/15 flex items-center justify-between gap-3 ring-1 ring-slate-950/5 dark:ring-white/5"
          >
            <!-- Status Indicator -->
            <div class="flex items-center gap-2 text-xs font-medium min-w-0">
              <span
                v-if="isSaving"
                class="inline-flex items-center gap-1.5 text-indigo-600 dark:text-blue-400 font-semibold truncate"
              >
                <Loader2Icon
                  class="h-4 w-4 animate-spin shrink-0 text-indigo-600 dark:text-blue-400"
                />
                <span class="truncate">Saving...</span>
              </span>

              <span
                v-else-if="hasUnsavedChanges"
                class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/50 px-2 py-0.5 sm:px-2.5 sm:py-1 border border-amber-200/80 dark:border-amber-800/40 text-amber-700 dark:text-amber-400 font-bold shrink-0 text-[11px] sm:text-xs"
              >
                <span
                  class="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse"
                ></span>
                <span>Unsaved</span>
              </span>

              <span
                v-else
                class="inline-flex items-center gap-1.5 text-slate-500 dark:text-[#A6B4C8] truncate text-[11px] sm:text-xs"
              >
                <CheckCircleIcon class="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span class="truncate">All changes saved</span>
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                class="rounded-xl border border-slate-200 dark:border-[#263449] bg-white dark:bg-[#18243A] px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-[#F1F5F9] hover:bg-slate-100 dark:hover:bg-[#1F2E47] transition active:scale-95 cursor-pointer"
                :disabled="isSaving"
                @click="confirmReset"
              >
                Discard
              </button>

              <button
                type="button"
                class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-3.5 sm:px-4 py-1.5 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none"
                :disabled="isSaving"
                @click="handleSave"
              >
                <Loader2Icon
                  v-if="isSaving"
                  class="h-3.5 w-3.5 animate-spin"
                />
                <SaveIcon
                  v-else
                  class="h-3.5 w-3.5"
                />
                <span class="whitespace-nowrap">{{
                  isSaving ? "Saving" : "Save Changes"
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </teleport>
    </main>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-2xl bg-white p-5 sm:p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset all prospectus download settings and
          footer customization back to their saved state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    Upload as UploadIcon,
    FileText as FileTextIcon,
    BookOpen as BookOpenIcon,
    LayoutTemplate as LayoutTemplateIcon,
    Eye as EyeIcon,
    Download as DownloadIcon,
  } from "lucide-vue-next";

  const store = useStore();

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);

  const prospectusFile = ref(null);
  const almanacFile = ref(null);
  const initialStateJson = ref("");

  const formData = reactive({
    prospectus: {
      title: "Download Prospectus 2026–27",
      description: "Curriculum, admissions and campus life — all in one place.",
      fileUrl: "",
      almanacUrl: "",
    },
    footer: {
      managementNote: "",
      copyrightText: "",
      poweredByText: "Powered by Educanium",
      poweredByLink: "https://educanium.com",
      usefulLinks: [],
      academicLinks: [],
    },
  });

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  function getCleanState() {
    return {
      prospectus: {
        title: formData.prospectus.title || "",
        description: formData.prospectus.description || "",
        fileUrl:
          typeof formData.prospectus.fileUrl === "string"
            ? formData.prospectus.fileUrl
            : "",
        almanacUrl:
          typeof formData.prospectus.almanacUrl === "string"
            ? formData.prospectus.almanacUrl
            : "",
      },
      footer: {
        managementNote: formData.footer.managementNote || "",
        copyrightText: formData.footer.copyrightText || "",
        poweredByText: formData.footer.poweredByText || "",
        poweredByLink: formData.footer.poweredByLink || "",
        usefulLinks: formData.footer.usefulLinks.map((l) => ({
          label: l.label || "",
          url: l.url || "",
          target: l.target || "_self",
        })),
        academicLinks: formData.footer.academicLinks.map((l) => ({
          label: l.label || "",
          url: l.url || "",
          target: l.target || "_self",
        })),
      },
      hasNewProspectusFile: prospectusFile.value instanceof File,
      hasNewAlmanacFile: almanacFile.value instanceof File,
    };
  }

  const hasUnsavedChanges = computed(() => {
    return JSON.stringify(getCleanState()) !== initialStateJson.value;
  });

  function snapshotState() {
    initialStateJson.value = JSON.stringify(getCleanState());
  }

  function handleFileSelect(event, type) {
    const file = event.target.files[0];
    if (!file) return;
    if (type === "prospectus") {
      prospectusFile.value = file;
    } else if (type === "almanac") {
      almanacFile.value = file;
    }
  }

  function addLink(column) {
    if (column === "useful") {
      formData.footer.usefulLinks.push({
        label: "",
        url: "#",
        target: "_self",
      });
    } else {
      formData.footer.academicLinks.push({
        label: "",
        url: "#",
        target: "_self",
      });
    }
  }

  function removeLink(column, index) {
    if (column === "useful") {
      formData.footer.usefulLinks.splice(index, 1);
    } else {
      formData.footer.academicLinks.splice(index, 1);
    }
  }

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details =
        store.state.website?.details || store.state.websiteDetails || {};

      const prospectus = details.prospectus || {};
      formData.prospectus.title =
        prospectus.title || "Download Prospectus 2026–27";
      formData.prospectus.description =
        prospectus.description ||
        "Curriculum, admissions and campus life — all in one place.";
      formData.prospectus.fileUrl =
        prospectus.fileUrl || details.prospectusLink || "";
      formData.prospectus.almanacUrl =
        prospectus.almanacUrl || details.almanacLink || "";

      const footer = details.footer || {};
      formData.footer.managementNote = footer.managementNote || "";
      formData.footer.copyrightText =
        footer.copyrightText || details.footerCopyright || "";
      formData.footer.poweredByText =
        footer.poweredByText || "Powered by Educanium";
      formData.footer.poweredByLink =
        footer.poweredByLink || "https://educanium.com";
      formData.footer.usefulLinks =
        Array.isArray(footer.usefulLinks) && footer.usefulLinks.length > 0
          ? footer.usefulLinks.map((l) => ({
              label: l.label || "",
              url: l.url || "#",
              target: l.target || "_self",
            }))
          : [
              { label: "Home", url: "/", target: "_self" },
              { label: "About Us", url: "/about", target: "_self" },
              { label: "Infrastructure", url: "#facilities", target: "_self" },
              { label: "Careers", url: "#", target: "_self" },
            ];

      formData.footer.academicLinks =
        Array.isArray(footer.academicLinks) && footer.academicLinks.length > 0
          ? footer.academicLinks.map((l) => ({
              label: l.label || "",
              url: l.url || "#",
              target: l.target || "_self",
            }))
          : [
              { label: "Curriculum", url: "#", target: "_self" },
              { label: "Preparatory Stage", url: "#", target: "_self" },
              { label: "Secondary Stage", url: "#", target: "_self" },
              { label: "Annual Planner", url: "#", target: "_self" },
            ];

      prospectusFile.value = null;
      almanacFile.value = null;
      snapshotState();
    } catch (err) {
      showToast("Failed to load prospectus & footer data", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const handleSave = async () => {
    isSaving.value = true;
    try {
      const payload = {
        prospectus: {
          title: formData.prospectus.title,
          description: formData.prospectus.description,
          fileUrl: prospectusFile.value || formData.prospectus.fileUrl,
          almanacUrl: almanacFile.value || formData.prospectus.almanacUrl,
        },
        footer: {
          managementNote: formData.footer.managementNote,
          copyrightText: formData.footer.copyrightText,
          poweredByText: formData.footer.poweredByText,
          poweredByLink: formData.footer.poweredByLink,
          usefulLinks: formData.footer.usefulLinks,
          academicLinks: formData.footer.academicLinks,
        },
      };

      await store.dispatch("updateWebsiteDetails", payload);
      await loadData();
      showToast("Prospectus & Footer details saved successfully!", "success");
    } catch (err) {
      showToast(err.message || "Error saving details", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Reset to saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }
</style>
